import { yupResolver } from '@hookform/resolvers/yup';
import { addWeeks } from 'date-fns';
import { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import {
  EventQuery,
  EventType,
  Milestone,
} from '../../../../apollo/__generated__/graphql';
import { useCreateMilestoneMutation } from '../../../hooks/useCreateMilestoneMutation';

export interface CreateMilestoneInputs {
  name: string;
  prices: number[];
  noAlcoholDiscount?: number;
  bedroomValue?: number;
  startAt: Date;
  endAt: Date;
  isVoucherOnly: boolean;
}

export const useCreateMilestoneDrawer = (
  event: EventQuery['event'],
  onClose: () => void,
  otherMilestones: Milestone[],
) => {
  const { createMilestone } = useCreateMilestoneMutation(() => {
    onClose();
  });

  const schema = yup
    .object({
      name: yup.string().required('Le nom du palier est requis'),
      prices: yup
        .array()
        .of(yup.number().required('Le prix est requis'))
        .length(event.numberOfDays)
        .required('Le prix est requis'),
      noAlcoholDiscount: yup.number().optional(),
      bedroomValue: yup.number().optional(),
      startAt: yup.date().required('La date de début est requise'),
      endAt: yup.date().required('La date de fin est requise'),
      isVoucherOnly: yup.boolean().required(),
    })
    .required();

  const priceOptions = useMemo(() => {
    return new Array(event.numberOfDays)
      .fill(0)
      .map((_, i) => i)
      .reverse();
  }, [event]);

  const defaultValues = useMemo(() => {
    if (otherMilestones.length === 0) {
      const prices = priceOptions.map((_, i) => (i + 1) * 2650);
      return {
        name: 'Palier 0',
        noAlcoholDiscount: 500,
        bedroomValue: 700,
        isVoucherOnly: false,
        prices,
      };
    }
    const lastMilestone = otherMilestones[otherMilestones.length - 1];

    return {
      name: `Palier ${otherMilestones.length}`,
      noAlcoholDiscount: lastMilestone.noAlcoholDiscount ?? 0,
      bedroomValue: lastMilestone.bedroomValue ?? 0,
      startAt: lastMilestone.endAt,
      endAt: addWeeks(lastMilestone.endAt, 2),
      isVoucherOnly: false,
      prices: lastMilestone.prices.map((price) => price.value + 500),
    };
  }, [otherMilestones, priceOptions]);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<CreateMilestoneInputs>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const onValid: SubmitHandler<CreateMilestoneInputs> = async (data) => {
    createMilestone({
      name: data.name,
      noAlcoholDiscount: data.noAlcoholDiscount,
      bedroomValue:
        event.type === EventType.BedroomAndTent ? data.bedroomValue : null,
      startAt: data.startAt,
      endAt: data.endAt,
      eventId: event.id,
      isVoucherOnly: data.isVoucherOnly,
      prices: data.prices.filter((price) => price !== undefined),
    });
  };

  const onSubmit = handleSubmit(onValid);

  const startAtValue = getValues('startAt');
  const endAtValue = getValues('endAt');

  const onStartAtChange = (date: Date | null) => {
    setValue('startAt', date ?? new Date());
  };

  const onEndAtChange = (date: Date | null) => {
    setValue('endAt', date ?? new Date());
  };

  return {
    onSubmit,
    onStartAtChange,
    onEndAtChange,
    startAtValue,
    endAtValue,
    register,
    errors,
    priceOptions,
  };
};
